// /src/App.js
import { useState, useEffect } from 'react';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import Dashboard from './pages/Dashboard';
import StudySession from './pages/StudySession';
import NavBar from './components/NavBar';
import ProfilePage from './pages/ProfilePage';
import ReviewSession from './pages/ReviewSession';
import BetaEnrollmentPage from './pages/BetaEnrollmentPage';

import {Helmet} from 'react-helmet-async';
import './App.css'

import { SubscriptionContext } from './SubscriptionContext';
import SubscriptionManagement from './components/SubscriptionManagement';

const BASE_URL = 'https://rocketprep-backend-399338419505.us-west2.run.app';
const supportEmail = 'rocketprepai+support@gmail.com'
const suggestionsEmail = 'rocketprepai+suggestions@gmail.com'

const App = () => {
  const { user, loading } = useAuth();
  const [userSubscription, setUserSubscription] = useState(null);
  const [appMessage, setAppMessage] = useState('');
  const navigate = useNavigate();



  const fetchUserSubscriptionData = async (firebaseUser) => {
    if (!firebaseUser) {
      console.log("No Firebase user available to fetch subscription data.");
      setUserSubscription(null);
      return;
    }

    try {
      const idToken = await firebaseUser.getIdToken(); // Get ID token from Firebase User object
      const response = await fetch(`${BASE_URL}/user/subscription`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch user subscription data from backend');
      }

      const data = await response.json();
      setUserSubscription(data);
      console.log("Fetched user subscription data:", data);
    } catch (error) {
      console.error("Error fetching user subscription data:", error);
      setAppMessage(`Error: ${error.message}`);
      setUserSubscription(null); // Clear subscription on error
    }
  };

  // Effect to fetch subscription data when user auth state changes
  useEffect(() => {
    if (user) { // Only fetch if user is logged in (not null)
      fetchUserSubscriptionData(user);
    } else if (!loading) { // If not loading and no user, clear subscription
      setUserSubscription(null);
    }
  }, [user, loading]); // Depend on user and loading from useAuth()


  const createCheckoutSession = async (planType, referralCode = null) => {
    if (!user) {
      setAppMessage("Please log in to subscribe.");
      return;
    }

    setAppMessage(`Initiating checkout for ${planType} plan...`);

    try {
      const idToken = await user.getIdToken();

      const requestBody = {
        plan_type: planType, // Pass the abstract plan type
        user_id: user.uid,
        success_url: `${window.location.origin}/success`,
        cancel_url: `${window.location.origin}/cancel`
      };

      // Add referral code if provided
      if (referralCode) {
        requestBody.referral_code = referralCode;
      }

      const response = await fetch(`${BASE_URL}/checkout/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session.');
      }

      const { session_url } = await response.json();
      window.location.href = session_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setAppMessage(`Error: ${error.message}`);
    }
  };

  const createCustomerPortalSession = async () => {
    if (!user) {
        throw new Error("User not authenticated.");
    }
    try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${BASE_URL}/stripe/create-customer-portal-session`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            // No body needed as user_id is derived from token on backend
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create customer portal session');
        }

        const {portal_url}  = await response.json();
        window.location.href = portal_url;
    } catch (error) {
        console.error("Error creating customer portal session:", error);
        throw error;
    }
};

const showUpgradeBanner = false;//userSubscription &&
//userSubscription.subscription_tier === 'free';
const freeQuestionsRemaining = userSubscription ?
(userSubscription.question_limit - (userSubscription.free_questions_used || 0)) : 0;

  if (loading) return <p>Loading...</p>; // while checking auth
  return (
    <SubscriptionContext.Provider value = {{userSubscription, createCheckoutSession, createCustomerPortalSession}}>
        <Helmet>
          <title>RocketPrep | AI Powered SAT Prep</title>
          <meta name="description" content="AI Powered SAT prep with advanced user analytics and real-life level questions! RocketPrepAI is designed to offer users a better, cheaper, and more capable AI test-preparation solution than what existing services offer."></meta>

          <meta name="og:title" content="RocketPrep | AI Powered SAT Prep"></meta>
          <meta name="og:description" content="AI Powered SAT prep with advanced user analytics and real-life level questions! RocketPrepAI is designed to offer users a better, cheaper, and more capable AI test-preparation solution than what existing services offer."></meta>
          <meta property="og:url" content="https://rocketprep.ai/" />
          <meta property="og:type" content="website" />
        </Helmet>
        <div classname="app-container">
          <NavBar />
          <div classname="main-content">
          {appMessage && <div className="app-message">{appMessage}</div>} {/* Display app-wide messages */}
          {showUpgradeBanner && (
        <div className="upgrade-banner">
          <div className="banner-content">
            <span className="font-bold">Limited Access!</span> You have <span className="font-bold">{freeQuestionsRemaining}</span> questions left in your Free tier.
            Upgrade for unlimited questions!
          </div>
          <div className="banner-buttons">
            <button
              onClick={() => navigate('/subscription-management')}
              className="upgrade-button"
            >
              Upgrade to Standard
            </button>
          </div>
 
        </div>


              
      )}
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/study-session" element={<StudySession />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/review-session/:sessionId" element={<ReviewSession />}/>
            <Route path="/subscription-management" element={<SubscriptionManagement />} />
          </Routes>
          <div className='app-footer-support'>
<p>
If you have any questions or need assistance, feel free to{' '}
<a href={`mailto:${supportEmail}`} className="support-email-link">
  contact our support team
</a>
.
If you have any suggestions/features you would like to see us add, please {' '} 
<a href={`mailto:${suggestionsEmail}`} className='suggestions-email-link'>
  send us a message
</a>
.
</p>

  </div>
          </div>
        </div>

    </SubscriptionContext.Provider>

  );
};

export default App;
